/**
 * Layer Service
 *
 * Frontend API client for managing layers via REST API.
 * Communicates with backend layersRouter.ts.
 */

import { Layer, CreateLayerInput, UpdateLayerInput, LayerFilters, LayerType, LayerCategory, BulkCreateResult } from '../types/layer';
import { getBackendUrl, isDevMode } from './configService';

// Get backend URL from runtime config (fail-fast, no fallback)
const getApiBaseUrl = (): string => {
  const url = getBackendUrl();
  if (!url) {
    throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
  }
  return url;
};

export class LayerService {
  private get baseUrl(): string {
    return `${getApiBaseUrl()}/api/layers`;
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    const token = localStorage.getItem('access_token');

    if (!token && isDevMode()) {
      return 'dev-mode-token';
    }

    return token;
  }

  /**
   * Build headers with authentication
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    // Only add Authorization header if we have a REAL token
    // Don't send fake 'dev-mode-token' - let cookie handle auth instead
    if (token && token !== 'dev-mode-token') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get all layers with optional filtering
   */
  async getAllLayers(filters?: LayerFilters): Promise<Layer[]> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.enabled !== undefined) params.append('enabled', String(filters.enabled));
        if (filters.category) params.append('category', filters.category);
        if (filters.layer_group) params.append('layer_group', filters.layer_group);
        if (filters.type) params.append('type', filters.type);
        if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
        if (filters.search) params.append('search', filters.search);
      }

      const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch layers: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch layers');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching layers:', error);
      throw error;
    }
  }

  /**
   * Get a single layer by ID
   */
  async getLayerById(id: string): Promise<Layer> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch layer: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch layer');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching layer:', error);
      throw error;
    }
  }

  /**
   * Get the default (enabled) background layer for map initialization.
   * Returns the enabled background, or falls back to first available if none enabled.
   * Returns null on failure for graceful degradation (caller should use fallback style).
   */
  async getDefaultBackground(): Promise<{ layer: Layer | null; fallback: boolean } | null> {
    try {
      const response = await fetch(`${this.baseUrl}/backgrounds/default`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn('[LayerService] Could not fetch default background:', response.status);
        return null;
      }

      const result = await response.json();

      if (!result.success) {
        console.warn('[LayerService] Default background request unsuccessful');
        return null;
      }

      return {
        layer: result.data,
        fallback: result.fallback,
      };
    } catch (error) {
      console.warn('[LayerService] Error fetching default background:', error);
      return null; // Graceful degradation - caller will use fallback
    }
  }

  /**
   * Create a new layer
   */
  async createLayer(input: CreateLayerInput): Promise<Layer> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Failed to create layer: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create layer');
      }

      return result.data;
    } catch (error) {
      console.error('Error creating layer:', error);
      throw error;
    }
  }

  /**
   * Bulk create multiple layers
   * Skips duplicates by name and returns summary of results
   */
  async bulkCreateLayers(
    layers: CreateLayerInput[],
    options: { overwrite?: boolean } = {},
  ): Promise<BulkCreateResult> {
    try {
      const response = await fetch(`${this.baseUrl}/bulk`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ layers, overwrite: !!options.overwrite }),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Failed to bulk create layers: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to bulk create layers');
      }

      return {
        created: result.created,
        updated: result.updated ?? 0,
        skipped: result.skipped,
        errors: result.errors,
        layers: result.layers,
      };
    } catch (error) {
      console.error('Error bulk creating layers:', error);
      throw error;
    }
  }

  /**
   * Apply the same field update to many layers in a single request.
   * `enabled` / `default_opacity` patch the top-level columns.
   * `refresh_interval_minutes` / `refresh_time_range` are virtual: the
   * backend merges them into the `config` jsonb as `refreshInterval`
   * (ms) and `refreshTimeRange` ("HH:MM-HH:MM" NWT) respectively.
   */
  async bulkUpdate(
    ids: string[],
    updates: {
      enabled?: boolean;
      default_opacity?: number;
      refresh_interval_minutes?: number;
      refresh_time_range?: string;
    },
  ): Promise<{ updated: Layer[] }> {
    try {
      const response = await fetch(`${this.baseUrl}/bulk`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ ids, updates }),
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.message || `Failed to bulk-update layers: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to bulk-update layers');
      }

      return { updated: result.updated };
    } catch (error) {
      console.error('Error bulk-updating layers:', error);
      throw error;
    }
  }

  /**
   * Update an existing layer
   */
  async updateLayer(id: string, input: UpdateLayerInput): Promise<Layer> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Failed to update layer: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to update layer');
      }

      return result.data;
    } catch (error) {
      console.error('Error updating layer:', error);
      throw error;
    }
  }

  /**
   * Delete a layer
   */
  async deleteLayer(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete layer: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete layer');
      }

      return true;
    } catch (error) {
      console.error('Error deleting layer:', error);
      throw error;
    }
  }

  /**
   * Get all layer groups
   */
  async getLayerGroups(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/groups`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch layer groups: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch layer groups');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching layer groups:', error);
      throw error;
    }
  }

  /**
   * Get all tags
   */
  async getAllTags(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseUrl}/tags`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tags: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch tags');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  }

  /**
   * Bulk update layer orders
   */
  async updateLayerOrders(orders: { id: string; order: number }[]): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/orders`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ orders }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update layer orders: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to update layer orders');
      }

      return true;
    } catch (error) {
      console.error('Error updating layer orders:', error);
      throw error;
    }
  }

  /**
   * Get GeoServer capabilities
   */
  async getGeoServerCapabilities(): Promise<any> {
    try {
      // Step 1: Fetch individual layers from REST API
      // /rest/layers.json returns ONLY individual layers, NOT groups
      const layersResponse = await fetch(`${getApiBaseUrl()}/api/geoserver/geoserver/rest/layers.json`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      const individualLayerNames = new Set<string>();
      if (layersResponse.ok) {
        const layersData = await layersResponse.json();
        console.log('[LayerService] Individual layers data:', layersData);

        // Extract individual layer names
        if (layersData.layers && layersData.layers.layer) {
          const layers = Array.isArray(layersData.layers.layer)
            ? layersData.layers.layer
            : [layersData.layers.layer];

          layers.forEach((layer: any) => {
            if (layer.name) {
              individualLayerNames.add(layer.name);
              console.log('[LayerService] Found individual layer:', layer.name);
            }
          });
        }
      } else {
        console.warn('[LayerService] Could not fetch individual layers from REST API:', layersResponse.status);
      }

      // Step 2: Fetch WMS capabilities (includes both layers AND groups)
      const serviceResponse = await fetch(
        `${getApiBaseUrl()}/api/geoserver/geoserver/ows?service=WMS&request=GetCapabilities`,
        {
          method: 'GET',
          headers: this.getHeaders(),
          credentials: 'include',
        }
      );

      if (!serviceResponse.ok) {
        throw new Error(`Failed to fetch WMS capabilities: ${serviceResponse.status}`);
      }

      const serviceXml = await serviceResponse.text();
      console.log('[LayerService] WMS XML received:', serviceXml.substring(0, 200));

      // Step 3: Parse WMS capabilities
      // Anything in GetCapabilities that's NOT in individualLayerNames is a group
      const wmsLayers = this.parseWMSCapabilities(serviceXml, individualLayerNames);

      console.log(`[LayerService] Total layers found: ${wmsLayers.length}`);

      // Step 4: Fetch child layers for each group from REST API
      const groups = wmsLayers.filter(layer => layer.isGroup);
      console.log(`[LayerService] Found ${groups.length} layer groups, fetching child layers...`);

      await Promise.all(
        groups.map(async (group) => {
          try {
            const childLayers = await this.fetchLayerGroupChildren(group.name);
            group.childLayers = childLayers;
            console.log(`[LayerService] Group ${group.name} has ${childLayers.length} child layers`);
          } catch (error) {
            console.warn(`[LayerService] Could not fetch children for group ${group.name}:`, error);
            // Keep empty array if fetch fails
          }
        })
      );

      // Step 5: Fetch resource type info for individual layers (not groups)
      const individualLayers = wmsLayers.filter(layer => !layer.isGroup);
      console.log(`[LayerService] Found ${individualLayers.length} individual layers, fetching resource types...`);

      await Promise.all(
        individualLayers.map(async (layer) => {
          try {
            const resourceInfo = await this.fetchLayerResourceType(layer.name);
            layer.resourceType = resourceInfo.resourceType;
            layer.geometryType = resourceInfo.geometryType;

            // Determine available import types based on resource type
            if (resourceInfo.resourceType === 'featureType') {
              layer.availableTypes = ['wms', 'wfs']; // Can import as WMS or WFS
            } else if (resourceInfo.resourceType === 'coverage') {
              layer.availableTypes = ['wms']; // Raster only supports WMS
            } else {
              layer.availableTypes = ['wms']; // Default to WMS if unknown
            }

            console.log(`[LayerService] Layer ${layer.name}: ${resourceInfo.resourceType}, geometry: ${resourceInfo.geometryType || 'N/A'}`);
          } catch (error) {
            console.warn(`[LayerService] Could not fetch resource type for ${layer.name}:`, error);
            layer.resourceType = 'unknown';
            layer.availableTypes = ['wms']; // Default to WMS on error
          }
        })
      );

      return { layers: wmsLayers };
    } catch (error) {
      console.error('Error fetching GeoServer capabilities:', error);
      throw error;
    }
  }

  /**
   * Fetch child layers for a layer group from GeoServer REST API
   */
  private async fetchLayerGroupChildren(groupName: string): Promise<string[]> {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/geoserver/geoserver/rest/layergroups/${groupName}.json`,
        {
          method: 'GET',
          headers: this.getHeaders(),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch layer group details: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[LayerService] Layer group ${groupName} details:`, data);

      // Extract child layer names from the response
      // GeoServer REST API returns child layers in layerGroup.publishables.published[]
      const childLayers: string[] = [];

      if (data.layerGroup && data.layerGroup.publishables && data.layerGroup.publishables.published) {
        const published = Array.isArray(data.layerGroup.publishables.published)
          ? data.layerGroup.publishables.published
          : [data.layerGroup.publishables.published];

        published.forEach((item: any) => {
          if (item.name) {
            childLayers.push(item.name);
          }
        });
      }

      return childLayers;
    } catch (error) {
      console.error(`Error fetching layer group children for ${groupName}:`, error);
      return [];
    }
  }

  /**
   * Fetch detailed resource type info for a layer from GeoServer REST API
   * Determines if layer is a featureType (WFS-capable vector) or coverage (WMS-only raster)
   */
  private async fetchLayerResourceType(layerName: string): Promise<{
    resourceType: 'featureType' | 'coverage' | 'unknown';
    geometryType?: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon' | 'GeometryCollection';
  }> {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/geoserver/geoserver/rest/layers/${layerName}.json`,
        {
          method: 'GET',
          headers: this.getHeaders(),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        console.warn(`[LayerService] Could not fetch resource type for ${layerName}: ${response.status}`);
        return { resourceType: 'unknown' };
      }

      const data = await response.json();
      console.log(`[LayerService] Layer ${layerName} resource info:`, data);

      // Check resource type from layer.resource
      const resourceType = data.layer?.resource?.['@class'];

      if (resourceType && resourceType.includes('featureType')) {
        // This is a vector layer - try to get geometry type from attributes
        const geometryAttribute = data.layer?.resource?.attributes?.attribute?.find(
          (attr: any) => attr.name && (attr.binding?.includes('Geometry') || attr.binding?.includes('Point') || attr.binding?.includes('LineString') || attr.binding?.includes('Polygon'))
        );

        let geometryType: any = undefined;
        if (geometryAttribute?.binding) {
          const binding = geometryAttribute.binding;
          if (binding.includes('Point')) geometryType = 'Point';
          else if (binding.includes('MultiPoint')) geometryType = 'MultiPoint';
          else if (binding.includes('LineString')) geometryType = 'LineString';
          else if (binding.includes('MultiLineString')) geometryType = 'MultiLineString';
          else if (binding.includes('Polygon')) geometryType = 'Polygon';
          else if (binding.includes('MultiPolygon')) geometryType = 'MultiPolygon';
          else if (binding.includes('Geometry')) geometryType = 'GeometryCollection';
        }

        return {
          resourceType: 'featureType',
          geometryType
        };
      } else if (resourceType && resourceType.includes('coverage')) {
        return { resourceType: 'coverage' };
      }

      return { resourceType: 'unknown' };
    } catch (error) {
      console.error(`[LayerService] Error fetching resource type for ${layerName}:`, error);
      return { resourceType: 'unknown' };
    }
  }

  /**
   * Parse WMS GetCapabilities XML to extract layer information
   */
  private parseWMSCapabilities(xml: string, individualLayerNames: Set<string>): any[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'text/xml');

    const layers: any[] = [];

    // Get the root Capability layer
    const rootLayer = doc.querySelector('Capability > Layer');
    if (!rootLayer) {
      console.warn('[LayerService] No root layer found in WMS capabilities');
      return layers;
    }

    // Recursively parse layer tree
    const parseLayer = (layerEl: Element, parentTitle?: string): void => {
      const name = layerEl.querySelector(':scope > Name')?.textContent;
      const title = layerEl.querySelector(':scope > Title')?.textContent;
      const abstract = layerEl.querySelector(':scope > Abstract')?.textContent;
      const queryable = layerEl.getAttribute('queryable') === '1';

      // Determine if this is a layer group using the REST API data
      // If the name is NOT in individualLayerNames, it's a group
      const isGroup = name ? !individualLayerNames.has(name) : false;

      const childLayers = Array.from(layerEl.querySelectorAll(':scope > Layer'));

      // Extract styles (only for queryable layers or non-group layers)
      const styles: any[] = [];
      if (queryable || !isGroup) {
        const styleElements = layerEl.querySelectorAll(':scope > Style');
        styleElements.forEach((styleEl) => {
          const styleName = styleEl.querySelector('Name')?.textContent;
          const styleTitle = styleEl.querySelector('Title')?.textContent;
          if (styleName) {
            styles.push({
              name: styleName,
              title: styleTitle || styleName,
            });
          }
        });
      }

      // Extract CRS/SRS
      const crs: string[] = [];
      const crsElements = layerEl.querySelectorAll(':scope > CRS');
      crsElements.forEach((crsEl) => {
        const crsValue = crsEl.textContent;
        if (crsValue) crs.push(crsValue);
      });
      const srsElements = layerEl.querySelectorAll(':scope > SRS');
      srsElements.forEach((srsEl) => {
        const srsValue = srsEl.textContent;
        if (srsValue) crs.push(srsValue);
      });

      // Extract bounding box
      let boundingBox = null;
      const bboxEl = layerEl.querySelector(':scope > BoundingBox[CRS="EPSG:4326"], :scope > BoundingBox[SRS="EPSG:4326"]');
      if (bboxEl) {
        boundingBox = {
          minx: parseFloat(bboxEl.getAttribute('minx') || '0'),
          miny: parseFloat(bboxEl.getAttribute('miny') || '0'),
          maxx: parseFloat(bboxEl.getAttribute('maxx') || '0'),
          maxy: parseFloat(bboxEl.getAttribute('maxy') || '0'),
          crs: bboxEl.getAttribute('CRS') || bboxEl.getAttribute('SRS') || 'EPSG:4326',
        };
      }

      // Extract geographic bounds
      let geographicBounds = null;
      const latLonBboxEl = layerEl.querySelector(':scope > LatLonBoundingBox');
      if (latLonBboxEl) {
        geographicBounds = {
          west: parseFloat(latLonBboxEl.getAttribute('minx') || '0'),
          south: parseFloat(latLonBboxEl.getAttribute('miny') || '0'),
          east: parseFloat(latLonBboxEl.getAttribute('maxx') || '0'),
          north: parseFloat(latLonBboxEl.getAttribute('maxy') || '0'),
        };
      } else {
        const exGeoBboxEl = layerEl.querySelector(':scope > EX_GeographicBoundingBox');
        if (exGeoBboxEl) {
          geographicBounds = {
            west: parseFloat(exGeoBboxEl.querySelector('westBoundLongitude')?.textContent || '0'),
            south: parseFloat(exGeoBboxEl.querySelector('southBoundLatitude')?.textContent || '0'),
            east: parseFloat(exGeoBboxEl.querySelector('eastBoundLongitude')?.textContent || '0'),
            north: parseFloat(exGeoBboxEl.querySelector('northBoundLatitude')?.textContent || '0'),
          };
        }
      }

      // Add this layer/group to the list
      if (title) { // Must have at least a title
        const layerInfo: any = {
          name: name || title, // Some groups don't have names
          title: title,
          abstract: abstract || '',
          type: 'WMS',
          workspace: name && name.includes(':') ? name.split(':')[0] : '',
          layerName: name && name.includes(':') ? name.split(':')[1] : name || title,
          styles: styles,
          crs: crs,
          boundingBox: boundingBox,
          geographicBounds: geographicBounds,
          isGroup: isGroup,
          queryable: queryable,
          parentGroup: parentTitle || null,
        };

        // Add children info for groups
        if (isGroup) {
          layerInfo.childLayers = childLayers.map(child =>
            child.querySelector(':scope > Name')?.textContent ||
            child.querySelector(':scope > Title')?.textContent ||
            'Unknown'
          );
        }

        layers.push(layerInfo);
      }

      // Recursively parse child layers (for all layers, not just groups)
      if (childLayers.length > 0) {
        childLayers.forEach(child => parseLayer(child, title));
      }
    };

    // Start parsing from root layer's children
    const topLevelLayers = Array.from(rootLayer.querySelectorAll(':scope > Layer'));
    topLevelLayers.forEach(layer => parseLayer(layer));

    console.log(`[LayerService] Parsed ${layers.length} layers/groups from WMS capabilities`);

    return layers;
  }

  /**
   * Test layer connection
   */
  async testLayerConnection(layerName: string, layerType?: string, bbox?: string, crs?: string): Promise<any> {
    try {
      console.log('[LayerService] Testing layer connection:', { layerName, layerType, bbox, crs });

      const response = await fetch(`${getApiBaseUrl()}/api/geoserver/layers/test`, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ layerName, layerType, bbox, crs }),
      });

      console.log('[LayerService] Test response status:', response.status);

      const result = await response.json();
      console.log('[LayerService] Test response data:', result);

      return result;
    } catch (error) {
      console.error('[LayerService] Error testing layer connection:', error);
      throw error;
    }
  }
}
